import useAuth from '@/contexts/useAuth';
import adaIcon from '@/assets/ada-icon.jpg';
import {
  CHAT_PAGE_PATH,
  ADMIN_USERS,
  MANAGEMENT_ROUTES,
} from '@/utils/constants';
import { dynamoDBService } from '@/utils/dynamodb';
import {
  Database,
  PlusCircle,
  Phone,
  PhoneCallIcon,
  Pen,
  FileText,
  Target,
  Calculator,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface SideBarProps {
  onLoadConversation?: any;
}

export const SideBar: React.FC<SideBarProps> = () => {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isManagementRoute = MANAGEMENT_ROUTES.includes(location.pathname);
  const [isManagementOpen, setIsManagementOpen] =
    useState<boolean>(isManagementRoute);
  const [showHelp, setShowHelp] = useState<boolean>(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>('');

  // Get username from profile or Cognito
  const username = auth.userProfile?.email || auth.user?.username;
  const isAdmin = username && ADMIN_USERS.includes(username);

  // Check if user has a profile (either localStorage or Cognito)
  if (!auth.isAuthenticated) {
    return null;
  }

  const handleNewChat = () => {
    if (location.pathname === CHAT_PAGE_PATH) {
      globalThis.location.reload();
    } else {
      navigate(CHAT_PAGE_PATH);
    }
  };

  return (
    <div className="h-full max-h-screen flex flex-col">
      <div className="flex-1 flex flex-col px-0 min-h-0">
        {/* New Chat Button */}
        <button
          onClick={handleNewChat}
          className="w-full text-left rounded-md px-2 py-2 flex items-center gap-2 text-gray-900 text-sm font-semibold hover:bg-teal-500/20 bg-gray-50 mb-2"
        >
          <PlusCircle className="h-4 w-4 text-gray-900 mr-2" />
          <span className="flex-1">New Chat</span>
        </button>

        {/* Ada Icon */}
        <div className="flex justify-center py-4">
          <img
            src={adaIcon}
            alt="Ada Icon"
            className="w-32 h-32 object-contain"
          />
        </div>

        {/* Admin Management Menu */}
        {/* TEMPORARILY DISABLED - Security issue: localStorage profiles can fake admin emails */}
        {/*
         {isAdmin && (
          <>
            <button
              type="button"
              className="w-full text-left text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mt-2 mb-2"
              onClick={() => setIsManagementOpen((prev) => !prev)}
              aria-expanded={isManagementOpen}
            >
              <Database className="h-4 w-4 mr-2 text-gray-900" />
              <span className="flex-1">Management</span>
              <svg
                className="w-4 h-4 ml-1 transition-transform duration-150 transform rotate-90"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            {isManagementOpen && (
              <div>
                <Link
                  to="/dashboard"
                  className={`text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mb-1 ${
                    location.pathname === '/dashboard' ? 'bg-teal-100' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/conversations"
                  className={`text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mb-1 ${
                    location.pathname === '/conversations' ? 'bg-teal-100' : ''
                  }`}
                >
                  Conversations
                </Link>
                <Link
                  to="/document-usage"
                  className={`text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mb-1 ${
                    location.pathname === '/document-usage' ? 'bg-teal-100' : ''
                  }`}
                >
                  Document Usage
                </Link>
                <Link
                  to="/feedback-global"
                  className={`text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mb-1 ${
                    location.pathname === '/feedback-global'
                      ? 'bg-teal-100'
                      : ''
                  }`}
                >
                  Global Feedback
                </Link>
                {SUPER_ADMIN_USER.includes(username) && (
                  <Link
                    to="/test-results"
                    className={`text-gray-900 text-sm font-semibold hover:bg-teal-500/20 rounded-md px-2 py-1 flex items-center mb-1 ${
                      location.pathname === '/test-results' ? 'bg-teal-100' : ''
                    }`}
                  >
                    Test Results
                  </Link>
                )}
              </div>
            )}
          </>
        )} */}
      </div>

      <div className="space-y-2">
        {/* Compliance Banner */}
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-3 mb-3">
          <p className="text-sm font-medium text-amber-900 text-center leading-snug">
            When using Ada, please do not enter any personal information from
            customers or others
          </p>
        </div>
        {/* Source Documents Card */}
        <nav
          aria-label="Source Documents"
          className="bg-white border-2 border-gray-400 rounded-lg p-3 mb-3"
        >
          <h3 className="text-sm font-bold text-gray-900 mb-2 w-full flex items-center justify-center">
            Source Documents
          </h3>
          <div className="space-y-2">
            <a
              href="https://www.allianzretireplus.com.au/forms-resources/product-guides.html"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center rounded-md bg-gray-50 text-gray-900 text-sm font-semibold hover:bg-teal-500/20 px-2 py-2 transition-colors my-2"
            >
              <FileText className="h-4 w-4 text-gray-900 mr-2 flex-shrink-0" />
              Product Disclosure Statements
            </a>
            <a
              href="https://www.allianzretireplus.com.au/forms-resources/product-guides.html"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center rounded-md bg-gray-50 text-gray-900 text-sm font-semibold hover:bg-teal-500/20 px-2 py-2 transition-colors my-2"
            >
              <Target className="h-4 w-4 text-gray-900 mr-2 flex-shrink-0" />
              Target Market Determinations
            </a>
            <a
              href="https://allianzretireplus-agile-rates.advice.openmarkets.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center rounded-md bg-gray-50 text-gray-900 text-sm font-semibold hover:bg-teal-500/20 px-2 py-2 transition-colors my-2"
            >
              <Calculator className="h-4 w-4 text-gray-900 mr-2 flex-shrink-0" />
              Rates Centre
            </a>
          </div>
        </nav>
        {/* Bottom Section */}
        <div className="w-full px-3 mt-4">
          {showHelp && (
            <div className="bg-blue-50 rounded-xl p-4 flex flex-col items-center shadow-sm relative my-2">
              <span className="text-gray-700 text-sm mb-3 text-center">
                Would you prefer to <b>speak</b> to one of our Allianz Retire+
                team <b>directly</b>?
              </span>
              <span className="text-gray-700 text-sm mb-3 text-left">
                We're here to <b>help</b>
              </span>

              <div className="w-full mt-3 flex flex-col gap-2">
                <a
                  href="https://www.allianzretireplus.com.au/contact.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center rounded-md bg-white text-gray-900 text-sm font-semibold hover:bg-teal-500/20 px-4 py-2 transition-colors"
                  aria-label="Contact Allianz Retire+"
                >
                  <Phone className="h-4 w-4 text-gray-900 mr-2" />
                  Contact Us
                </a>
              </div>
            </div>
          )}
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="w-full text-left rounded-md px-2 py-2 flex items-center text-gray-900 text-sm font-semibold hover:bg-teal-500/20 bg-gray-50 my-2"
          >
            <PhoneCallIcon className="h-4 w-4 text-gray-900 mr-2" />
            <span className="flex-1">Talk to us</span>
            <svg
              className={`w-4 h-4 ml-1 transition-transform duration-150 ${showHelp ? 'transform -rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Give Feedback button */}
          <button
            onClick={() => setFeedbackDialogOpen(true)}
            className="w-full text-left rounded-md px-2 py-2 flex items-center text-gray-900 text-sm font-semibold hover:bg-teal-500/20 bg-gray-50"
            aria-label="Give global feedback"
          >
            <Pen className="h-4 w-4 text-gray-900 mr-2" />
            Give Feedback
          </button>
        </div>
      </div>

      {/* Global feedback dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onOpenChange={(open) => setFeedbackDialogOpen(open)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Share general feedback about the bot or app. This will be sent to
              the dev team.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="What would you like to tell us?"
              className="w-full border rounded p-2 min-h-[120px] text-sm"
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              className="px-3 py-1 rounded bg-gray-200"
              onClick={() => setFeedbackDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              className="px-3 py-1 rounded bg-teal-600 text-white"
              onClick={async () => {
                if (!feedbackText || feedbackText.trim().length === 0) {
                  toast.error('Please enter feedback text');
                  return;
                }

                try {
                  const userId = username || 'anonymous';

                  // Only save if user is admin with Cognito access
                  if (auth.user) {
                    const credentials = await auth.getCredentials();
                    await dynamoDBService.initializeClient(credentials);
                    await dynamoDBService.saveGeneralFeedback(
                      userId,
                      feedbackText.trim(),
                    );
                    toast.success('Thanks — your feedback was submitted');
                  } else {
                    toast.info('Feedback noted (admin login required to save)');
                  }

                  setFeedbackDialogOpen(false);
                  setFeedbackText('');
                } catch (err) {
                  console.error('Error sending feedback:', err);
                  toast.error('Failed to submit feedback');
                }
              }}
            >
              Submit
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
